import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { Autocomplete, Button, TextField, Typography } from "@mui/material";
import { Box, Stack } from "@mui/system";
import CheckIcon from "@mui/icons-material/Check";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
} from "@mui/material";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import DataStatusAlert from "../../../../Health/DataAccuracy/UI_HelpersOf_DataAccuracy/DataStatusAlert_Helpers";
import { useDispatch, useSelector } from "react-redux";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../../features/userSlice";
import { BusinessInsightsEndPointLogic } from "../../../../../../logic/AssetManagement/BusinessInsights/BusinessInsightsEndPointLogic";
import store from "../../../../../../store";
import dayjs from "dayjs";
import SimpleBackdrop from "../../../../../BackDrop/BackDrop";
import { useSearchParams } from "react-router-dom";

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function ChangeStatusPopup({ open, onClose }: Props) {
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const [comment, setComment] = React.useState<string>("");
  const taskDetails = useSelector(
    (state: any) => state.businessInsights.taskDetails
  );
  const { selectedSearchSite } = useSelector(
    (state: any) => state?.businessInsights
  );
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [status, setStatus] = React.useState(null) as any;
  const [selectedDate, setSelectedDate] = React.useState<string | null>(null);
  const [searchParams,] = useSearchParams();
  const insightType = searchParams.get("insightType");

  const StatusList = [
    { label: "To Do", value: "to_do" },
    { label: "In Progress", value: "blocked" },
    { label: "Snooze", value: "snoozed" },
    { label: "Done", value: "done" },
  ];

  const handleCloseChangeStatusModal = () => {
    // Close the Change Status modal and clear the comment
    onClose(); // Close modal if success
    setComment(""); // Clear comment
    setStatus(null); // Clear status
    setSelectedDate(null); // Clear selected date
  };

  const changeStatusBodyActionItem = {
    shared_uid: taskDetails.shared_uid,
    current_status: status,
    snooze_end: selectedDate
      ? dayjs(selectedDate).format("YYYY-MM-DD HH:mm:ss")
      : null,
  };

  const handleActions = async () => {
    if (!status || !comment || (status === "Snooze" && !selectedDate)) return; // Prevent action if status or comment is not set
    setLoading(true); // Set loading state to true
    try {
      if (oauthTokenHeader && changeStatusBodyActionItem) {
        await BusinessInsightsEndPointLogic.updateBusinessInsightsStatus(
          oauthTokenHeader,
          changeStatusBodyActionItem,
          taskDetails?.shared_uid
        ); // Update the business insight status
        await BusinessInsightsEndPointLogic.postCommentBusinessInsightTask(
          oauthTokenHeader,
          { object_type: "tasks", comment },
          taskDetails?.shared_uid
        ); // Post the comment
        await BusinessInsightsEndPointLogic.getTaskDetails(
          oauthTokenHeader,
          taskDetails?.shared_uid
        ); // Refresh task details
        await BusinessInsightsEndPointLogic.getAllBusinessInsights(
          selectedSearchSite,
          oauthTokenHeader,
          dispatch
        );
      }
    } catch (error: any) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message:
            error.message ||
            "An error occurred while changing the insight status.",
          severity: "error",
        })
      );
    } finally {
      setLoading(false); // Reset loading state
      handleCloseChangeStatusModal(); // Close modal after action
      store.dispatch(
        setDisplayErrorSnackBar({
          message: "Bussiness Insight status has been changed to " + status,
          type: "success",
        })
      );
      setComment(""); // Clear comment after action
      setStatus(null); // Clear status after action
      setSelectedDate(null); // Clear selected date after action
    }
  };

  // useEffect hook to reset fields when the modal opens
  React.useEffect(() => {
    if (open) {
      setComment(""); // Reset comment when modal opens
      setStatus(null); // Reset status when modal opens
      setSelectedDate(null); // Reset selected date when modal opens
    }
  }, [open]);

  return (
    <React.Fragment>
      {loading ? (
        <SimpleBackdrop />
      ) : (
        <Dialog
          open={open}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          maxWidth="md"
          fullWidth
        >
          <DialogTitle id="alert-dialog-title">
            {"Change Insight Status"}
          </DialogTitle>
          <DialogContent>
            <TableContainer sx={{ marginBottom: "16px" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ padding: "6px 0px" }}>
                      <Typography fontWeight={500}>Task Name</Typography>
                    </TableCell>
                    <TableCell sx={{ padding: "6px 0px" }}>
                      <Typography fontWeight={500}>Assignee</Typography>
                    </TableCell>
                    <TableCell sx={{ padding: "6px 0px" }}>
                      <Typography fontWeight={500}>Status</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {taskDetails && (
                    <TableRow>
                      <TableCell sx={{ padding: "6px 0px" }}>
                        {taskDetails.data["query_output:sec_title"].value}
                      </TableCell>
                      <TableCell sx={{ padding: "6px 0px" }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar
                            alt={taskDetails.assignee_fullname}
                            src=""
                            sx={{ width: 24, height: 24 }}
                          />
                          <Typography variant="body2">
                            {taskDetails.assignee_fullname}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell padding="none">
                        <DataStatusAlert status={taskDetails.current_status} />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <DialogContentText id="alert-dialog-description">
              <Stack
                direction="row"
                gap={1}
                alignItems="flex-start"
                mt={3}
                mb={2}
              >
                <Autocomplete
                  disablePortal={false}
                  options={StatusList}
                  value={
                    StatusList.find((option) => option.label === status) || null
                  }
                  getOptionLabel={(option: any) => option.label}
                  onChange={(_, newValue) =>
                    setStatus(newValue ? newValue.label : null)
                  }
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      sx={{
                        paddingTop: 1,
                        "& .MuiInputLabel-root": {
                          top: 8,
                        },
                      }}
                      {...params}
                      label="Status"
                      size="small"
                      required
                      helperText={"This field is mandatory"}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                      placeholder="Choose Status from the list"
                      color="secondary"
                    />
                  )}
                />
                <Box sx={{ width: "100%" }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DemoContainer components={["DatePicker"]}>
                      <DatePicker
                        label="Status Action Date"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                            color: "secondary",
                            required: status === "Snooze",
                            helperText:
                              status === "Snooze"
                                ? "This field is mandatory for Snoozed status"
                                : "",
                            inputProps: {
                              readOnly: true,
                              onKeyDown: (e) => {
                                // Block ALL keys including numbers and delete
                                e.preventDefault();
                                e.stopPropagation();
                                return false;
                              },
                              onKeyPress: (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                return false;
                              },
                              onKeyUp: (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                return false;
                              },
                              onInput: (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                return false;
                              },
                              onBeforeInput: (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                return false;
                              },
                              onPaste: (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                return false;
                              },
                              onCut: (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                return false;
                              },
                              onChange: (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                return false;
                              },
                              onFocus: (e) => {
                                e.target.blur(); // Remove focus immediately
                              },
                              style: {
                                pointerEvents: "none",
                                caretColor: "transparent",
                                userSelect: "none",
                                WebkitUserSelect: "none",
                                MozUserSelect: "none",
                                cursor: "default",
                              },
                            },
                            sx: {
                              "& .MuiInputBase-input": {
                                pointerEvents: "none !important",
                                caretColor: "transparent !important",
                                userSelect: "none !important",
                                WebkitUserSelect: "none !important",
                                MozUserSelect: "none !important",
                                "&:focus": {
                                  outline: "none !important",
                                  boxShadow: "none !important",
                                },
                              },
                              "& .MuiIconButton-root": {
                                pointerEvents: "auto !important",
                                cursor: "pointer !important",
                              },
                            },
                          },
                        }}
                        format="DD/MM/YYYY"
                        maxDate={dayjs("2030-12-31")}
                        //minDate={dayjs('' + new Date().getFullYear() + '-01-01')}
                        minDate={dayjs()} // Prevent selection of past dates
                        disablePast // Disables all past dates automatically
                        onChange={(date: any) => {
                          if (date) {
                            setSelectedDate(
                              dayjs(date).format("YYYY-MM-DD HH:mm:ss")
                            );
                          }
                        }}
                      />
                    </DemoContainer>
                  </LocalizationProvider>
                </Box>
              </Stack>
              <TextField
                size="small"
                label="Reason"
                fullWidth
                placeholder="Explain why or how this task is resolved"
                color="secondary"
                required
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
              />
            </DialogContentText>
            <Stack direction="row" gap={1} justifyContent="flex-end" mt={2}>
              <Button variant="text" color="secondary" onClick={onClose}>
                CANCEL
              </Button>
              <Button
                variant="contained"
                color="secondary"
                loading={loading}
                loadingPosition="end"
                disabled={
                  !status || !comment || (status === "Snooze" && !selectedDate)
                }
                data-tracker={insightType === "adgroup" ? "323" : "344"}
                endIcon={loading && <CheckIcon fontSize="small" />}
                onClick={() => handleActions()}
              >
                Change Status
                <CheckIcon fontSize="small" />
              </Button>
            </Stack>
          </DialogContent>
        </Dialog>
      )}
    </React.Fragment>
  );
}
